import React, { useState } from "react";
import { ArrowRight, ShoppingBag, Sparkles, Check } from "lucide-react";

/**
 * Design Preview — three palette + type explorations for the site rebrand.
 * Product-forward layouts inspired by klipschindia.com.
 * Not linked from Nav; visit /design-preview manually.
 */

const OPTIONS = [
  {
    id: "heritage-copper",
    name: "Heritage Copper",
    tag: "Warm · Cataloguey · Klipsch-adjacent",
    // colors
    bg: "#F5F1EA",        // ivory
    surface: "#FFFFFF",   // card
    ink: "#1A1A1A",       // near black
    muted: "#6B6357",     // warm gray
    accent: "#B85E28",    // signature copper
    accent2: "#8B3E15",   // deep copper for hover
    borderRgba: "0,0,0,0.08",
    // type
    displayFont: "'Cormorant Garamond', serif",
    bodyFont: "'Manrope', sans-serif",
    kickerCase: "uppercase",
    radius: "6px",
    vibe: "Cream backdrop, matte-black speaker photography pops, copper CTAs. Warm, heritage-craft, showroom-first.",
  },
  {
    id: "editorial-cinema",
    name: "Editorial Cinema",
    tag: "Dark · Editorial · Cinematic",
    bg: "#0A0A0A",
    surface: "#141414",
    ink: "#EDE7DA",
    muted: "#8A8478",
    accent: "#C87533",     // burnt copper
    accent2: "#6E1F1F",    // oxblood
    borderRgba: "255,255,255,0.08",
    displayFont: "'Fraunces', serif",
    bodyFont: "'Manrope', sans-serif",
    kickerCase: "uppercase",
    radius: "2px",
    vibe: "Near-black + warm ivory. Sharp corners, big serif display, cinema-magazine energy. A darker, moodier alternative to current gold theme.",
  },
  {
    id: "showroom-white",
    name: "Showroom White",
    tag: "Minimal · Modern · B&O-like",
    bg: "#FFFFFF",
    surface: "#F5F5F5",
    ink: "#0F0F0F",
    muted: "#6B6B6B",
    accent: "#A67C00",     // deep brass
    accent2: "#1F3A2E",    // forest sophistication
    borderRgba: "0,0,0,0.06",
    displayFont: "'Manrope', sans-serif",
    bodyFont: "'Manrope', sans-serif",
    kickerCase: "uppercase",
    radius: "12px",
    vibe: "Pure white gallery. Bold sans display. Product photography carries the visual weight. Bang & Olufsen / Apple Store energy.",
  },
];

const SAMPLE_PRODUCTS = [
  { name: "Klipsch The Fives", cat: "Powered Bookshelf", price: "₹99,800", was: "₹1,22,900", off: "18%", img: "https://images.unsplash.com/photo-1545454675-3531b543be5d?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "The Sevens Matte Black", cat: "Tower Speaker", price: "₹1,64,800", was: "₹2,02,900", off: "18%", img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Flexus Core 200", cat: "Dolby Atmos Soundbar", price: "₹66,800", was: "₹82,300", off: "18%", img: "https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

function Tile({ opt, picked, onPick }) {
  const s = {
    "--bg": opt.bg,
    "--surface": opt.surface,
    "--ink": opt.ink,
    "--muted": opt.muted,
    "--accent": opt.accent,
    "--accent2": opt.accent2,
    "--border": `rgba(${opt.borderRgba})`,
    "--radius": opt.radius,
    "--display": opt.displayFont,
    "--body": opt.bodyFont,
  };

  return (
    <div className="rounded-3xl overflow-hidden border border-white/10 flex flex-col" data-testid={`preview-tile-${opt.id}`}>
      {/* Preview canvas */}
      <div
        style={{
          ...s,
          background: "var(--bg)",
          color: "var(--ink)",
          fontFamily: "var(--body)",
        }}
        className="p-8"
      >
        {/* Fake nav */}
        <div className="flex items-center justify-between mb-8" style={{ color: "var(--ink)" }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full border" style={{ borderColor: "var(--accent)" }}>
              <div className="w-1.5 h-1.5 rounded-full m-2.5" style={{ background: "var(--accent)" }}></div>
            </div>
            <span style={{ fontFamily: "var(--display)", fontSize: 18, letterSpacing: 2 }}>BATRA</span>
          </div>
          <button
            style={{
              background: "var(--accent)",
              color: opt.id === "heritage-copper" ? "#fff" : opt.id === "editorial-cinema" ? "#0A0A0A" : "#fff",
              padding: "8px 14px",
              borderRadius: "var(--radius)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            Get a Quote
          </button>
        </div>

        {/* Kicker + hero */}
        <div style={{ color: "var(--accent)", fontSize: 10, letterSpacing: 3, textTransform: opt.kickerCase, marginBottom: 8 }}>
          NEW · SPOTLIGHT
        </div>
        <h2 style={{ fontFamily: "var(--display)", fontSize: 40, lineHeight: 1.05, marginBottom: 12, color: "var(--ink)", fontWeight: opt.id === "showroom-white" ? 800 : 600 }}>
          Cinema, engineered <br />
          <span style={{ color: "var(--accent)", fontStyle: opt.id === "editorial-cinema" || opt.id === "heritage-copper" ? "italic" : "normal" }}>for your room.</span>
        </h2>
        <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.6, marginBottom: 20, maxWidth: 380 }}>
          Reference-grade home theatre from Klipsch, KEF and Bowers & Wilkins — curated and calibrated by Batra Electronics, Dehradun since 1985.
        </p>

        {/* Product row */}
        <div className="grid grid-cols-3 gap-3">
          {SAMPLE_PRODUCTS.map((p, i) => (
            <div
              key={i}
              style={{
                background: "var(--surface)",
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
                overflow: "hidden",
              }}
            >
              <div style={{ aspectRatio: "1/1", background: opt.id === "editorial-cinema" ? "#0F0F0F" : "#EDEBE6", position: "relative" }}>
                <img src={p.img} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    fontSize: 9,
                    padding: "3px 8px",
                    background: "var(--accent)",
                    color: opt.id === "editorial-cinema" ? "#0A0A0A" : "#fff",
                    borderRadius: 999,
                    fontWeight: 700,
                  }}
                >
                  {p.off} OFF
                </span>
              </div>
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 9, letterSpacing: 1.5, color: "var(--muted)", textTransform: "uppercase", marginBottom: 4 }}>{p.cat}</div>
                <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: "var(--ink)", marginBottom: 6, minHeight: 32 }}>{p.name}</div>
                <div className="flex items-baseline gap-2">
                  <span style={{ color: "var(--ink)", fontWeight: 700, fontSize: 13 }}>{p.price}</span>
                  <span style={{ color: "var(--muted)", fontSize: 10, textDecoration: "line-through" }}>{p.was}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary CTAs */}
        <div className="flex gap-3 mt-6">
          <button
            style={{
              background: "var(--accent)",
              color: opt.id === "editorial-cinema" ? "#0A0A0A" : "#fff",
              padding: "12px 20px",
              borderRadius: "var(--radius)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            SHOP ALL <ArrowRight className="w-3 h-3" />
          </button>
          <button
            style={{
              background: "transparent",
              color: "var(--ink)",
              padding: "12px 20px",
              borderRadius: "var(--radius)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 1,
              border: `1px solid ${opt.id === "editorial-cinema" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            BOOK A DEMO
          </button>
        </div>
      </div>

      {/* Meta strip */}
      <div className="bg-charcoal-900 p-6 space-y-3 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-2xl text-white">{opt.name}</div>
            <div className="text-xs text-white/50 uppercase tracking-[0.18em] mt-1">{opt.tag}</div>
          </div>
          <button
            onClick={() => onPick(opt.id)}
            className={`px-5 py-2.5 rounded-full text-xs font-semibold transition inline-flex items-center gap-2 ${
              picked ? "bg-gold text-black" : "border border-white/20 text-white hover:border-gold hover:text-gold"
            }`}
            data-testid={`pick-${opt.id}`}
          >
            {picked ? <><Check className="w-3.5 h-3.5" /> Picked</> : "Pick this"}
          </button>
        </div>

        {/* Swatches */}
        <div className="flex gap-2 pt-2">
          {[
            { label: "BG", c: opt.bg },
            { label: "SURFACE", c: opt.surface },
            { label: "INK", c: opt.ink },
            { label: "ACCENT", c: opt.accent },
            { label: "ACCENT-2", c: opt.accent2 },
          ].map((sw) => (
            <div key={sw.label} className="flex-1">
              <div className="h-10 rounded-md border border-white/10" style={{ background: sw.c }}></div>
              <div className="text-[9px] text-white/40 mt-1 tracking-wider">{sw.label}</div>
              <div className="text-[10px] text-white/70 font-mono">{sw.c}</div>
            </div>
          ))}
        </div>

        <p className="text-xs text-white/60 leading-relaxed pt-2">{opt.vibe}</p>

        <div className="grid grid-cols-2 gap-2 pt-1 text-[10px] text-white/50">
          <div>Display: <span className="text-white/80">{opt.displayFont.replace(/'/g, "").split(",")[0]}</span></div>
          <div>Body: <span className="text-white/80">{opt.bodyFont.replace(/'/g, "").split(",")[0]}</span></div>
          <div>Radius: <span className="text-white/80">{opt.radius}</span></div>
          <div>Layout: <span className="text-white/80">Product-forward</span></div>
        </div>
      </div>
    </div>
  );
}

export default function DesignPreview() {
  const [picked, setPicked] = useState(null);

  return (
    <div className="pt-32 pb-24 container-x" data-testid="design-preview">
      <div className="max-w-3xl mb-12">
        <div className="kicker mb-3">Design Exploration · Rebrand</div>
        <h1 className="font-display text-5xl md:text-6xl text-white">Pick a <em className="not-italic gold-text">direction.</em></h1>
        <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">
          Three palette + layout explorations, product-forward, inspired by Klipsch India. Same hero, same three products — different worlds. Tap "Pick this" on the one that resonates and we'll roll it across the entire site.
        </p>
      </div>

      <div className="grid xl:grid-cols-3 lg:grid-cols-2 gap-6">
        {OPTIONS.map((o) => (
          <Tile key={o.id} opt={o} picked={picked === o.id} onPick={setPicked} />
        ))}
      </div>

      {picked && (
        <div className="mt-10 glass rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4" data-testid="picked-summary">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-gold" />
            <div>
              <div className="text-xs text-white/50 uppercase tracking-[0.2em]">You picked</div>
              <div className="font-display text-2xl text-white">{OPTIONS.find((o) => o.id === picked).name}</div>
            </div>
          </div>
          <p className="text-sm text-white/60 max-w-md">
            Reply in chat with "<span className="text-gold">go with {OPTIONS.find((o) => o.id === picked).name}</span>" and I'll apply this across the entire site (Home, Shop, Product Detail, Packages, Cart, Contact, Quote, Footer).
          </p>
        </div>
      )}

      {!picked && (
        <div className="mt-10 text-center text-white/40 text-sm">
          Tap "Pick this" on your preferred direction, or reply in chat which one you prefer.
        </div>
      )}
    </div>
  );
}
